// The numbers at the top of a quotation's, an order's or a purchase order's page (boards DocQuote,
// DocQuotePhone, DocPO; docs/plans/doc-pages.md): a thin bar, the customer or supplier as a
// card, and the steps of "Where it stands".
import { Card, Skeleton, cx } from '@ims/ui'
import type { ReactNode } from 'react'
import { Link } from 'react-router'
import type { StepState } from '../lib/sales-orders.ts'

export type MeterTone = 'primary' | 'success' | 'warning' | 'danger'

const FILL: Record<MeterTone, string> = {
  primary: 'bg-primary',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
}

/** How far along something is, as a thin bar; `label` says it for screen readers. */
export function Meter({
  pct,
  tone = 'primary',
  label,
  className,
}: {
  pct: number
  tone?: MeterTone
  label: string
  className?: string
}) {
  return (
    <span
      role="img"
      aria-label={label}
      className={cx('block h-1.5 overflow-hidden rounded-full bg-soft', className)}
    >
      <span
        className={cx('block h-full rounded-full', FILL[tone])}
        style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
      />
    </span>
  )
}

/** One of the four numbers: a label, the value, a bar under it when there is one, a hint. */
export function BandStat({
  label,
  value,
  hint,
  meter,
}: {
  label: string
  value: ReactNode
  hint?: ReactNode
  meter?: ReactNode
}) {
  return (
    <Card className="min-w-0 p-4">
      <p className="text-sm font-medium text-muted">{label}</p>
      <p className="tabular mt-1 truncate text-xl font-bold text-ink">{value}</p>
      {meter && <div className="mt-2">{meter}</div>}
      {hint && <p className="mt-1 text-xs text-muted">{hint}</p>}
    </Card>
  )
}

/** The customer or supplier: initials, the name linking to their page, two short lines. */
export function PartyCard({
  label,
  name,
  to,
  lines,
  area,
}: {
  label: string
  name: string | null
  to: string | null
  lines: ReactNode[]
  area: 'people' | 'buy'
}) {
  const initials = (name ?? '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase()
  return (
    <Card className="flex min-w-0 items-center gap-3 p-4">
      <span
        aria-hidden
        className={cx(
          'grid size-10 shrink-0 place-items-center rounded-full font-bold',
          area === 'people' ? 'bg-people-light text-people' : 'bg-buy-light text-buy',
        )}
      >
        {initials}
      </span>
      <div className="min-w-0">
        <p className="text-sm font-medium text-muted">{label}</p>
        {name === null ? (
          <Skeleton className="mt-1 h-5 w-32" />
        ) : to ? (
          <Link to={to} className="block truncate font-bold text-primary hover:underline">
            {name}
          </Link>
        ) : (
          <p className="truncate font-bold text-ink">{name}</p>
        )}
        {lines.filter(Boolean).map((l, i) => (
          <p key={i} className="truncate text-xs text-muted">
            {l}
          </p>
        ))}
      </div>
    </Card>
  )
}

const STEP_TONE: Record<StepState, string> = {
  done: 'bg-success-light text-success',
  now: 'bg-primary-light text-primary',
  later: 'bg-soft text-subtle',
  stopped: 'bg-danger-light text-danger',
}

/** A step of "Where it stands": an icon in its state's colour, a title, a note, and maybe buttons. */
export function Step({
  icon,
  state,
  title,
  note,
  children,
}: {
  icon: ReactNode
  state: StepState
  title: string
  note: string
  children?: ReactNode
}) {
  return (
    <li
      className="flex gap-3 border-t border-border py-2.5 first:border-t-0"
      aria-current={state === 'now' ? 'step' : undefined}
    >
      <span
        className={cx('grid size-8 shrink-0 place-items-center rounded-full', STEP_TONE[state])}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1 text-sm">
        <span className="block font-semibold">{title}</span>
        <span className="block text-muted">{note}</span>
        {children}
      </span>
    </li>
  )
}
